import * as THREE from 'three';

/**
 * 材质优化器模块
 * 针对数字人模型进行材质参数调优：皮肤质感、金属度、粗糙度、透明度
 * 通过遍历模型 Mesh，对皮肤、头发、衣物等不同部位分别设置材质参数
 */
export class MaterialOptimizer {
  /**
   * 优化数字人模型的全部材质
   * @param {THREE.Group | THREE.Object3D} model - 数字人模型
   * @param {object} [options] - 材质参数配置
   */
  static optimize(model, options = {}) {
    const {
      // 皮肤材质参数
      skinRoughness = 0.65,     // 皮肤粗糙度（越低越光滑）
      skinMetalness = 0.0,      // 皮肤金属度（皮肤无金属质感）
      // 头发材质参数
      hairRoughness = 0.4,
      hairMetalness = 0.1,
      // 衣物材质参数
      clothRoughness = 0.8,
      clothMetalness = 0.0,
      // 全局
      envMapIntensity = 1.0,    // 环境贴图反射强度
    } = options;

    model.traverse((child) => {
      if (!child.isMesh || !child.material) return;

      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];

      for (const material of materials) {
        this.#optimizeMaterial(material, child.name, {
          skinRoughness, skinMetalness,
          hairRoughness, hairMetalness,
          clothRoughness, clothMetalness,
          envMapIntensity,
        });
      }
    });
  }

  /**
   * 根据网格名称智能优化材质参数
   */
  static #optimizeMaterial(material, meshName, params) {
    const lowerName = meshName.toLowerCase();

    // 确保材质使用标准物理渲染（PBR）
    if (!material.isMeshStandardMaterial && !material.isMeshPhysicalMaterial) {
      return;
    }

    // 皮肤识别关键词
    const skinKeywords = ['skin', 'face', 'head', 'body', 'eye', 'lip', 'mouth', 'neck', 'hand', 'arm', 'leg'];
    // 头发识别关键词
    const hairKeywords = ['hair'];
    // 衣物识别关键词
    const clothKeywords = ['cloth', 'shirt', 'pants', 'shoe', 'sock', 'dress', 'jacket', 'coat'];

    const isSkin = skinKeywords.some(kw => lowerName.includes(kw));
    const isHair = hairKeywords.some(kw => lowerName.includes(kw));
    const isCloth = clothKeywords.some(kw => lowerName.includes(kw));

    if (isSkin) {
      material.roughness = params.skinRoughness;
      material.metalness = params.skinMetalness;
    } else if (isHair) {
      material.roughness = params.hairRoughness;
      material.metalness = params.hairMetalness;
    } else if (isCloth) {
      material.roughness = params.clothRoughness;
      material.metalness = params.clothMetalness;
    }

    // 环境反射强度
    if (material.envMapIntensity !== undefined) {
      material.envMapIntensity = params.envMapIntensity;
    }

    // 确保材质在色调映射下正确呈现
    material.needsUpdate = true;
  }

  /**
   * 快速设置模型整体材质参数（不分区，统一调整）
   * @param {THREE.Group} model
   * @param {object} params
   */
  static setGlobalMaterialParams(model, params = {}) {
    model.traverse((child) => {
      if (!child.isMesh || !child.material) return;

      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];

      for (const material of materials) {
        if (params.roughness !== undefined) material.roughness = params.roughness;
        if (params.metalness !== undefined) material.metalness = params.metalness;
        if (params.opacity !== undefined) {
          material.transparent = params.opacity < 1;
          material.opacity = params.opacity;
        }
        if (params.envMapIntensity !== undefined) {
          material.envMapIntensity = params.envMapIntensity;
        }
        material.needsUpdate = true;
      }
    });
  }
}
